import { Checkbox } from "@blueprintjs/core";
import { ButtonContainer, IDataTableColumn } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";

import { WorkbookTypeApi, WorkbookTypeGridDto, WorkbookTypeSearch } from "../../../../api";
import { ERoutes } from "../../../../ERoutes";
import { EditButton, SearchTablePage, ViewButton } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import styled from "styled-components";

const Center = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
`;

export interface IWorkbookTypeListPageProps {}
export const WorkbookTypeListPage: React.FunctionComponent<IWorkbookTypeListPageProps> = (props) => {
  const { t } = useTl();
  const history = useHistory();
  const api = useApiService(WorkbookTypeApi);
  const { currentLanguage } = useGlobalData();

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: WorkbookTypeGridDto) => (
          <ButtonContainer>
            <ViewButton
              minimal={true}
              onClick={() => history.push(`${ERoutes.workbookType}/${row.workbookTypeId}/detail/view`)}
            />
            <EditButton
              minimal={true}
              onClick={() => history.push(`${ERoutes.workbookType}/${row.workbookTypeId}/detail/edit`)}
            />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.Name),
        fieldName: "name",
        render: (row: WorkbookTypeGridDto) =>
          `${row.workbookColor} - ${row.hasPedigree ? t(ETLCodes.WithPedigree) : t(ETLCodes.WithoutPedigree)}`,
      },
      {
        header: () => t(ETLCodes.WorkbookColor),
        fieldName: "workbookColor",
      },
      {
        header: () => t(ETLCodes.HasPedigree),
        fieldName: "hasPedigree",
        alignment: "center",
        render: (row: WorkbookTypeGridDto) => <Checkbox checked={row.hasPedigree} disabled />,
      },
      {
        header: () => <Center>{t(ETLCodes.CanGiveWorkCertificate)}</Center>,
        fieldName: "canGiveWorkCertificate",
        alignment: "center",
        render: (row: WorkbookTypeGridDto) => <Checkbox checked={row.canGiveWorkCertificate} disabled />,
      },
      {
        header: () => <Center>{t(ETLCodes.HasSocialibilityTest)}</Center>,
        fieldName: "hasSocialibilityTest",
        alignment: "center",
        render: (row: WorkbookTypeGridDto) => <Checkbox checked={row.sociabilityTest} disabled />,
      },
      {
        header: () => t(ETLCodes.Description),
        fieldName: "description",
      },
    ],
    [history, t]
  );

  const addItemFunction = React.useCallback(() => {
    history.push(`${ERoutes.workbookType}/0/detail/edit`);
  }, [history]);

  const search = React.useCallback(
    (sObj) => api.baseSearch(WorkbookTypeSearch.fromJS({ ...sObj, languageId: currentLanguage.languageId })),
    [api, currentLanguage.languageId]
  );

  return (
    <SearchTablePage
      columns={columns}
      searchFunction={search}
      exportFunction={(sObj) =>
        api.exportXlsFile(WorkbookTypeSearch.fromJS({ ...sObj, languageId: currentLanguage.languageId }))
      }
      getCriteriasFunction={() => api.getSearchCriterias(true)}
      addFunc={addItemFunction}
      sortKeys={{ workbookColor: "ASC", hasPedigree: "ASC" }}
      breadCrumbs={[{ text: t(ETLCodes.WorkbookTypes) }]}
    />
  );
};
